// src/state/AuthContext.tsx
//
// Session state for the whole app. On mount it asks the gateway (`GET /api/me`)
// whether the httpOnly cookie already carries a live session — this app never
// stores the session itself, only what `/api/me` and `/api/session` hand back.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type {
  DemonstrationModeReport,
  IdentityProviderReport,
  NotificationDeliveryReport,
  SettlementDispatchReport,
} from '@prs2/contracts'
import { api, ApiError, isNetworkError, onSessionExpired } from '../lib/api'
import type { CurrentUser, SessionResponse } from '../lib/types'
import { NETWORK_FAILURE } from '../lib/messages'

type AuthStatus = 'checking' | 'signed-in' | 'signed-out' | 'unreachable'

interface AuthState {
  readonly status: AuthStatus
  readonly user: CurrentUser | null
  /** Set only when `status === 'unreachable'` — the gateway itself did not respond. */
  readonly unreachableReason: string | null
  /**
   * The gateway's own `APP_ENV`, as reported on `/api/me` (on mount, or after
   * `login()`). `null` only while `status === 'checking'`/`'unreachable'`, or
   * once signed out.
   *
   * ⚠ TASK 5f: `Shell.tsx` was this field's only consumer and no longer reads
   * it — see that file's own header. `LoginPage` never used this: it is
   * signed out by definition, so it reads the same value from its own
   * `api.identities()` call instead, for its own (unchanged) badge/alert —
   * see that page's own comment. Kept here, unread, because `/api/me` still
   * reports it and this is where the client mirrors what that response says.
   */
  readonly environment: string | null
  /**
   * WHAT THE GATEWAY SAID about which identity provider it installed, as
   * reported on the same responses. Held beside `environment` and NOT derived
   * from it: the two answer different questions — *where is this running* and
   * *what is authenticating its users* — and only the second one can justify
   * telling an officer that nothing on screen is real. See `lib/environment.ts`.
   *
   * ⚠ A REPORT, not a string, and `null` here means one thing only: **no
   * response has arrived** (`checking`/`unreachable`, or signed out). A response
   * that carried no usable value is a report of `null` and produces a DANGER
   * notice, not silence — see `requestSelfReporting` in `lib/api.ts` for the
   * fail-open this shape exists to make unrepresentable.
   */
  readonly identityProvider: IdentityProviderReport | null
  /**
   * WHAT THE GATEWAY SAID about whether anything done here can reach a Payment
   * Agency, from the same three responses. Held beside the other two and NOT
   * derived from either: it is the only witness for the second half of the
   * notice's sentence (S-016), and the `null` here means the same one thing —
   * **no response has arrived**. A response that carried no usable value is a
   * report of `null`, which withdraws the reassurance rather than granting it.
   */
  readonly settlementDispatch: SettlementDispatchReport | null
  /**
   * WHAT THE GATEWAY SAID about whether it is a live demonstration of the
   * production build, from the same three responses. Held beside the other
   * two and NOT derived from either. `null` means the same one thing it
   * means for the other two: **no response has arrived**.
   *
   * ⚠ TASK 5f: `Shell.tsx`'s demonstration banner — this field's only
   * consumer (Task 5a's `demonstrationBanner()` in `lib/environment.ts`) —
   * was deleted, not hidden: the Product Owner decided no clause in the
   * tender requires an on-screen demonstration notice. Nothing in
   * `apps/officer-web` reads this field any more. It is kept here, unread,
   * because `GET /api/me`/`POST /api/session` still report it and this is
   * where the client mirrors everything those responses say about
   * themselves — not because anything renders from it today.
   */
  readonly demonstrationMode: DemonstrationModeReport | null
  /**
   * ★ WHAT THE GATEWAY SAID about whether anything queued here can leave the
   * building as an email, from the same three responses.
   *
   * ⚠ Held here because a screen that is NOT the notifications screen needs it:
   * `EmailTemplatesPage` authors the content that gets sent and told a PSD user,
   * in bold, that *"no delivery mechanism exists anywhere in this system"* — a
   * claim that stopped being true four tasks into the notification-delivery wave
   * and that the notifications screen one click away already contradicts. That
   * page has no list response of its own carrying the capability, so it reads it
   * from here.
   *
   * ⚠ Same `null` discipline as the three above: `null` is **no response has
   * arrived**; a response that said nothing is a report of `null`, which
   * `discloseNotificationDelivery` treats as unknown rather than as containment.
   */
  readonly notificationDelivery: NotificationDeliveryReport | null
  login(email: string): Promise<void>
  /** ★ ADR 0015 — act as a demonstration account. The session is replaced; this adopts the new one. */
  assumeUser(userId: string): Promise<void>
  /** ★ ADR 0015 — step back to the administrator's own account. */
  assumeSelf(): Promise<void>
  /**
   * ★★ WAVE 6 — finish a federated authorization-code flow with the `code` and
   * `state` the identity provider put on the browser's own URL.
   *
   * ⚠ Separate from {@link AuthState.login} rather than an overload, because the
   * two are different acts with different risks: one asserts an email with no
   * password (a demonstration affordance) and the other redeems a one-time code
   * the gateway minted a challenge for. Collapsing them would make the call site
   * read as though a roster pick and a federated sign-in were interchangeable.
   */
  completeFederatedLogin(code: string, state: string): Promise<void>
  logout(): Promise<void>
  /** Re-run the initial `/api/me` check, e.g. after "Try again" on the unreachable state. */
  retry(): void
  /**
   * ★★★ S-079 — WHETHER THIS BROWSER ARRIVED AT `/login` BY SIGNING OUT ON PURPOSE,
   * rather than by being bounced off a page it was not signed in for.
   *
   * ⚠ **`LoginPage` needs to tell those two apart and the ROUTER CANNOT TELL IT.**
   * `RequireAuth` sends a signed-out visitor to `/login` with
   * `state={{ from: location }}`, which is right and is the deep-link feature: open
   * `/bills/X` unauthenticated, sign in, land on `/bills/X`. But `logout()` also
   * makes the status `signed-out`, so `RequireAuth` re-renders and captures the page
   * the OUTGOING officer was on — and the next officer to sign in is then sent
   * there. Measured by a browser journey: officer A referred `BILL-NUH-09`, signed
   * out, and officer B's first screen after signing in was `/bills/BILL-NUH-09`.
   *
   * The two cases are distinguishable only by whether the sign-out was DELIBERATE,
   * which is a fact only `logout()` holds. It cannot be carried in router state: the
   * `Navigate` inside `RequireAuth` and the `navigate()` inside the sign-out handler
   * race, and whichever lands last owns `location.state`. A context flag does not
   * race — it is set before the status change that triggers either navigation.
   *
   * `true` after `logout()`; `false` after any successful sign-in, and on the
   * initial session check.
   */
  readonly signedOutDeliberately: boolean
  /**
   * ★ WAVE 27 — the session ended ON ITS OWN (the server no longer knows it),
   * rather than the officer choosing to leave.
   *
   * ⚠ Deliberately NOT the negation of {@link signedOutDeliberately}: both are
   * `false` on the initial check, when nobody has signed in or out at all, and a
   * screen that said "your session ended" to a first-time visitor would be
   * telling them something that never happened.
   */
  readonly sessionExpired: boolean
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [environment, setEnvironment] = useState<string | null>(null)
  const [identityProvider, setIdentityProvider] = useState<IdentityProviderReport | null>(null)
  const [settlementDispatch, setSettlementDispatch] = useState<SettlementDispatchReport | null>(null)
  const [demonstrationMode, setDemonstrationMode] = useState<DemonstrationModeReport | null>(null)
  const [notificationDelivery, setNotificationDelivery] = useState<NotificationDeliveryReport | null>(null)
  const [unreachableReason, setUnreachableReason] = useState<string | null>(null)
  const [checkNonce, setCheckNonce] = useState(0)
  // ★★★ S-079 — see the field's docstring on the interface above. Starts `false`:
  // a fresh page load has not signed out, so a deep link must still be honoured.
  const [signedOutDeliberately, setSignedOutDeliberately] = useState(false)
  /**
   * ★ WAVE 27 — the session ENDED ON ITS OWN, rather than the officer leaving.
   * Read by `LoginPage` so it can say which of the two happened; a sign-in clears
   * it, and `logout()` never sets it.
   */
  const [sessionExpired, setSessionExpired] = useState(false)

  useEffect(() => {
    let cancelled = false
    setStatus('checking')
    api
      .me()
      .then((me) => {
        if (cancelled) return
        setUser({ id: me.id, ...(me.userId !== undefined && { userId: me.userId }), email: me.email, displayName: me.displayName, scope: me.scope, issuedAt: me.issuedAt, ...(me.canSwitchUser === true && { canSwitchUser: true }) })
        setEnvironment(me.environment)
        setIdentityProvider(me.identityProvider)
        setSettlementDispatch(me.settlementDispatch)
        setDemonstrationMode(me.demonstrationMode)
        setNotificationDelivery(me.notificationDelivery)
        setStatus('signed-in')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ApiError && error.status === 401) {
          setUser(null)
          setStatus('signed-out')
          return
        }
        setUnreachableReason(
          isNetworkError(error)
            ? NETWORK_FAILURE
            : error instanceof Error
              ? error.message
              : 'An unknown error occurred while checking the session.',
        )
        setStatus('unreachable')
      })
    return () => {
      cancelled = true
    }
  }, [checkNonce])

  /**
   * ★★ WAVE 6 — adopt a session, however it was established.
   *
   * ⚠ Extracted so the demo-roster login and the federated callback set the
   * SAME state in the SAME order. Two copies of this would be two places the
   * five self-reported capability values could drift, and this app has already
   * paid for a badge that appeared on one screen and not another.
   *
   * ⚠★ **AND `login` DID NOT ACTUALLY USE IT until 2026-08-22**, which is the drift
   * this comment was written to prevent, present the whole time it claimed to
   * prevent it. `login` set the same seven pieces of state inline, in the same
   * order — so the two paths agreed by coincidence rather than by construction.
   * Found while adding one more thing that had to happen on BOTH: S-079's flag
   * clear, which would have been added to the demo path alone and left the
   * federated callback sending the next officer to the previous one's page.
   * `login` now calls this.
   */
  const adopt = useCallback((result: SessionResponse) => {
    setUser({
      id: result.session.id,
      email: result.session.email,
      displayName: result.session.displayName,
      scope: result.scope,
      issuedAt: result.session.issuedAt,
      ...(result.canSwitchUser === true && { canSwitchUser: true }),
    })
    setEnvironment(result.environment)
    setIdentityProvider(result.identityProvider)
    setSettlementDispatch(result.settlementDispatch)
    setDemonstrationMode(result.demonstrationMode)
    setNotificationDelivery(result.notificationDelivery)
    // ★★★ S-079 — cleared on EVERY successful sign-in, so the flag describes the
    // current arrival at `/login` rather than a sign-out from an hour ago. Without
    // it, a sign-out followed by a sign-in and then a genuine deep link would send
    // the officer to the dashboard instead of the page they asked for.
    setSignedOutDeliberately(false)
    setStatus('signed-in')
  }, [])

  /**
   * ★★ WAVE 6 — finish the federated authorization-code flow.
   *
   * The gateway exchanges the code, verifies the ID token against the issuer's
   * JWKS, and maps the verified `email` claim to a local user through the SAME
   * `resolveLocalUserByEmail` the demo roster uses — so authorisation stays
   * database-driven regardless of how the identity arrived.
   */
  const completeFederatedLogin = useCallback(
    async (code: string, state: string) => {
      adopt(await api.completeFederatedLogin(code, state))
    },
    [adopt],
  )

  const assumeUser = useCallback(
    async (userId: string) => {
      adopt(await api.assumeUser(userId))
    },
    [adopt],
  )

  const assumeSelf = useCallback(async () => {
    adopt(await api.assumeSelf())
  }, [adopt])

  const login = useCallback(
    async (email: string) => {
      // ⚠ THROUGH `adopt`, not inline. See its header: this function carried its own
      // copy of the same seven assignments until 2026-08-22, which is exactly the
      // drift `adopt` was extracted to prevent.
      adopt(await api.login(email))
    },
    [adopt],
  )

  /**
   * ★★★ WAVE 27 — A SESSION THAT HAS GONE ENDS THE SESSION, ONCE, INSTEAD OF
   * FAILING FIFTY-TWO SURFACES SEPARATELY.
   *
   * ⚠ **The defect, from a demonstration screen.** The gateway keeps sessions in
   * memory, so restarting it invalidates every open tab's cookie. Every screen
   * kept asking; every answer was `401 a session is required`; and each caller
   * rendered its own error card with a Retry button that could never succeed. The
   * dashboard showed five at once — bills, audit, interface alerts, referrals,
   * paused — out of **52** such surfaces in the console. One fact, reported as a
   * pile of unrelated breakages.
   *
   * `api.request` now reports the fact once (`onSessionExpired`) and this is the
   * only listener: drop the client's copy of the session and go to `signed-out`,
   * which is exactly what `logout()` below does. `RequireAuth` then shows the sign
   * in screen, and the officer does the one thing that actually helps.
   *
   * ⚠ **`signedOutDeliberately` is NOT set, and that is the point of the
   * distinction S-079 introduced.** This was not a sign-out — the officer did not
   * ask to leave — so the page they were on IS carried, and signing back in
   * returns them to it. A deliberate sign-out deliberately does not carry it.
   *
   * ⚠ **Guarded on `status`.** The listener fires per failing request and a dead
   * session usually fails several at once; without the guard the state would be
   * set once per in-flight call. Reacting only while signed in also means a 401
   * arriving after the officer has already reached the login screen changes
   * nothing.
   */
  useEffect(
    () =>
      onSessionExpired(() => {
        setStatus((current) => {
          if (current !== 'signed-in') return current
          setUser(null)
          setEnvironment(null)
          setIdentityProvider(null)
          setSettlementDispatch(null)
          setDemonstrationMode(null)
          setNotificationDelivery(null)
          setSessionExpired(true)
          return 'signed-out'
        })
      }),
    [],
  )

  const logout = useCallback(async () => {
    // ★★★ S-079 — SET BEFORE THE STATUS CHANGES, not after, and that ordering is
    // the whole fix. `setStatus('signed-out')` is what makes `RequireAuth` render
    // its `<Navigate … state={{ from: location }}>`; a flag set afterwards would be
    // read by `LoginPage` only on a later render, and the redirect would already
    // have happened.
    setSignedOutDeliberately(true)
    setSessionExpired(false)
    try {
      await api.logout()
    } finally {
      // The cookie is cleared server-side either way; drop the client's copy of
      // the session regardless of whether the DELETE itself round-tripped, so a
      // network blip on logout never leaves the UI claiming to be signed in.
      setUser(null)
      setEnvironment(null)
      setIdentityProvider(null)
      setSettlementDispatch(null)
      setDemonstrationMode(null)
      setNotificationDelivery(null)
      setStatus('signed-out')
    }
  }, [])

  const retry = useCallback(() => setCheckNonce((n) => n + 1), [])

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      environment,
      identityProvider,
      settlementDispatch,
      demonstrationMode,
      notificationDelivery,
      unreachableReason,
      login,
      assumeUser,
      assumeSelf,
      completeFederatedLogin,
      logout,
      retry,
      signedOutDeliberately,
      sessionExpired,
    }),
    [
      status,
      user,
      environment,
      identityProvider,
      settlementDispatch,
      demonstrationMode,
      notificationDelivery,
      unreachableReason,
      login,
      assumeUser,
      assumeSelf,
      completeFederatedLogin,
      logout,
      retry,
      signedOutDeliberately,
      sessionExpired,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth() called outside <AuthProvider>')
  }
  return context
}
